import { format } from "date-fns";

/**
 * Pure side of the Volume block shared by Reflector V1 and V2 Logs (CTX-1485).
 * Everything here reads the backend's own histogram buckets; nothing is
 * aggregated from loaded log lines, which are only the first pages.
 */

export type VolumeLevel = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "TRACE";

/** Stack order, bottom to top. TRACE only shows up when a bucket carries it. */
export const VOLUME_LEVELS: VolumeLevel[] = [
  "TRACE",
  "DEBUG",
  "INFO",
  "WARNING",
  "ERROR",
];

/**
 * A bucket as either backend sends it: V1 counts `WARNING`, V2 counts `WARN`.
 * `bucket` is the interval's start, ISO 8601.
 */
export interface RawVolumeBucket {
  bucket: string;
  total: number;
  TRACE?: number;
  DEBUG?: number;
  INFO?: number;
  WARN?: number;
  WARNING?: number;
  ERROR?: number;
}

export interface VolumeBucket {
  start: Date;
  end: Date;
  counts: Record<VolumeLevel, number>;
  total: number;
}

export interface VolumeGap {
  /** Index of the first and last (inclusive) empty bucket of the run. */
  firstIndex: number;
  lastIndex: number;
  start: Date;
  end: Date;
}

export interface VolumePeak {
  bucket: VolumeBucket;
  warning: number;
  error: number;
}

export interface VolumeRange {
  start: Date;
  end: Date;
}

/** Fewer consecutive empty buckets than this are just short bars, not a gap. */
export const MIN_GAP_BUCKETS = 2;

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/** A bucket without a zone is UTC, as the backends write them. */
export function parseBucketStart(bucket: string): Date {
  return new Date(HAS_ZONE.test(bucket) ? bucket : `${bucket}Z`);
}

const count = (value: number | undefined): number => Number(value || 0);

/** The distance between consecutive starts, or `fallbackMs` with fewer than two. */
export function bucketWidthMs(starts: number[], fallbackMs: number): number {
  let width = Infinity;
  for (let i = 1; i < starts.length; i += 1) {
    const step = starts[i] - starts[i - 1];
    if (step > 0 && step < width) width = step;
  }
  return Number.isFinite(width) ? width : Math.max(fallbackMs, 1);
}

const emptyCounts = (): Record<VolumeLevel, number> => ({
  TRACE: 0,
  DEBUG: 0,
  INFO: 0,
  WARNING: 0,
  ERROR: 0,
});

/**
 * Sorts the buckets and gives each its end. An interval the backend left out
 * between two it did send is an empty one, so it is added with zero counts.
 * `fallbackMs` is the width when there is a single bucket (the range's span).
 */
export function normalizeBuckets(
  raw: RawVolumeBucket[] | null | undefined,
  fallbackMs: number
): VolumeBucket[] {
  if (!raw || raw.length === 0) return [];
  const sorted = raw
    .map((b) => ({ raw: b, startMs: parseBucketStart(b.bucket).getTime() }))
    .filter((b) => !Number.isNaN(b.startMs))
    .sort((a, b) => a.startMs - b.startMs);
  const width = bucketWidthMs(
    sorted.map((b) => b.startMs),
    fallbackMs
  );

  const out: VolumeBucket[] = [];
  const push = (startMs: number, counts: Record<VolumeLevel, number>, total: number) =>
    out.push({
      start: new Date(startMs),
      end: new Date(startMs + width),
      counts,
      total,
    });

  sorted.forEach(({ raw: b, startMs }) => {
    const previous = out[out.length - 1];
    if (previous) {
      for (let t = previous.start.getTime() + width; t < startMs; t += width) {
        push(t, emptyCounts(), 0);
      }
    }
    push(
      startMs,
      {
        TRACE: count(b.TRACE),
        DEBUG: count(b.DEBUG),
        INFO: count(b.INFO),
        WARNING: count(b.WARNING) + count(b.WARN),
        ERROR: count(b.ERROR),
      },
      count(b.total)
    );
  });
  return out;
}

/** Runs of consecutive empty buckets, only when the range has some logs. */
export function findGaps(
  buckets: VolumeBucket[],
  minRun: number = MIN_GAP_BUCKETS
): VolumeGap[] {
  if (!buckets.some((b) => b.total > 0)) return [];
  const gaps: VolumeGap[] = [];
  let runStart = -1;
  const close = (lastIndex: number) => {
    if (runStart >= 0 && lastIndex - runStart + 1 >= minRun) {
      gaps.push({
        firstIndex: runStart,
        lastIndex,
        start: buckets[runStart].start,
        end: buckets[lastIndex].end,
      });
    }
    runStart = -1;
  };
  buckets.forEach((b, i) => {
    if (b.total > 0) close(i - 1);
    else if (runStart < 0) runStart = i;
  });
  close(buckets.length - 1);
  return gaps;
}

/** The bucket with the most WARNING + ERROR (the earliest on a tie); null when all are 0. */
export function findPeak(buckets: VolumeBucket[]): VolumePeak | null {
  let best: VolumePeak | null = null;
  let bestScore = 0;
  for (const bucket of buckets) {
    const warning = bucket.counts.WARNING;
    const error = bucket.counts.ERROR;
    if (warning + error > bestScore) {
      bestScore = warning + error;
      best = { bucket, warning, error };
    }
  }
  return best;
}

/** Legend order of the design; TRACE comes last and only if a bucket has it. */
export function legendLevels(buckets: VolumeBucket[]): VolumeLevel[] {
  const base: VolumeLevel[] = ["DEBUG", "INFO", "WARNING", "ERROR"];
  return buckets.some((b) => b.counts.TRACE > 0) ? [...base, "TRACE"] : base;
}

/** "15 min", "2 h", "30 s": the width of one bucket for the subtitle. */
export function intervalLabel(widthMs: number): string {
  const HOUR = 3_600_000;
  const MINUTE = 60_000;
  if (widthMs < MINUTE) return `${Math.max(Math.round(widthMs / 1000), 1)} s`;
  if (widthMs >= HOUR && widthMs % HOUR === 0) return `${widthMs / HOUR} h`;
  return `${Math.round(widthMs / MINUTE)} min`;
}

/** Local time like the rest of the page. */
export function formatClock(date: Date): string {
  return format(date, "HH:mm");
}

/** "hh:mm–hh:mm"; a range over several days carries the date so it is not ambiguous. */
export function formatSpan(start: Date, end: Date): string {
  const sameDay = start.toDateString() === end.toDateString();
  const pattern = sameDay ? "HH:mm" : "dd MMM HH:mm";
  return `${format(start, pattern)}–${format(end, pattern)}`;
}

/** The range covering buckets `a` to `b` (either order), edges on bucket borders. */
export function rangeBetweenBuckets(
  buckets: VolumeBucket[],
  a: number,
  b: number
): VolumeRange {
  const first = Math.min(a, b);
  const last = Math.max(a, b);
  return { start: buckets[first].start, end: buckets[last].end };
}

/** Evenly spread bucket starts for the time axis. */
export function axisTicks(buckets: VolumeBucket[], maxTicks = 5): Date[] {
  if (buckets.length === 0) return [];
  const steps = Math.min(maxTicks, buckets.length);
  const indexes = new Set<number>();
  for (let i = 0; i < steps; i += 1) {
    indexes.add(
      steps === 1 ? 0 : Math.round((i * (buckets.length - 1)) / (steps - 1))
    );
  }
  return Array.from(indexes).map((i) => buckets[i].start);
}
